import { type ErrorMapCtx, type ZodIssueOptionalMessage, z } from "zod";
import { makeInflect } from "../../utils/makeInflect.js";
import { sameValueForEveryLocale } from "../../utils/sameValueForEveryLocale.js";
import { toDateTimeMonthYearFormat } from "../../utils/toDateTimeMonthYearFormat.js";
import { Locale, type LocalizedString } from "../Locale.js";

const inflectElement = {
	[Locale.cs]: makeInflect("prvek", "prvky", "prvků"),
	[Locale.en]: makeInflect("element", "elements", "elements"),
};
const inflectCharacter = {
	[Locale.cs]: makeInflect("znak", "znaky", "znaků"),
	[Locale.en]: makeInflect("character", "characters", "characters"),
};

function localizedErrorMap(issue: ZodIssueOptionalMessage, _ctx: ErrorMapCtx): { message: LocalizedString } {
	switch (issue.code) {
		case z.ZodIssueCode.invalid_string: {
			switch (issue.validation) {
				case "email": {
					return {
						message: {
							[Locale.cs]: "E-mailová adresa není platná",
							[Locale.en]: "E-mail address is not valid",
						},
					};
				}
				case "url": {
					return {
						message: {
							[Locale.cs]: "Nejedná se o platný odkaz",
							[Locale.en]: "Is not a valid URL",
						},
					};
				}
				case "emoji": {
					return {
						message: {
							[Locale.cs]: "Nejedná se o platné emoji",
							[Locale.en]: "Is not a valid emoji",
						},
					};
				}
				case "uuid": {
					return {
						message: {
							[Locale.cs]: "Nejedná se o platné UUID",
							[Locale.en]: "Is not a valid UUID",
						},
					};
				}
				case "nanoid": {
					return {
						message: {
							[Locale.cs]: "Nejedná se o platné Nano ID",
							[Locale.en]: "Is not a valid Nano ID",
						},
					};
				}
				case "regex": {
					return {
						message: {
							[Locale.cs]: "Pole nesplňuje požadovaný formát",
							[Locale.en]: "Field is not in the required format",
						},
					};
				}
				case "cuid": {
					return {
						message: {
							[Locale.cs]: "Nejedná se o platné CUID",
							[Locale.en]: "Is not a valid CUID",
						},
					};
				}
				case "cuid2": {
					return {
						message: {
							[Locale.cs]: "Nejedná se o platné CUID2",
							[Locale.en]: "Is not a valid CUID2",
						},
					};
				}
				case "ulid": {
					return {
						message: {
							[Locale.cs]: "Nejedná se o platné ULID",
							[Locale.en]: "Is not a valid ULID",
						},
					};
				}
				case "datetime": {
					return {
						message: {
							[Locale.cs]: "Nejedná se o platné datum a čas",
							[Locale.en]: "Is not a valid date time",
						},
					};
				}
				case "date": {
					return {
						message: {
							[Locale.cs]: "Nejedná se o platné datum",
							[Locale.en]: "Is not a valid date",
						},
					};
				}
				case "time": {
					return {
						message: {
							[Locale.cs]: "Nejedná se o platný čas",
							[Locale.en]: "Is not a valid time",
						},
					};
				}
				case "duration": {
					return {
						message: {
							[Locale.cs]: "Nejedná se o platnou dobu",
							[Locale.en]: "Is not a valid duration",
						},
					};
				}
				case "ip": {
					return {
						message: {
							[Locale.cs]: "Nejedná se o platnou IP adresu",
							[Locale.en]: "Is not a valid IP address",
						},
					};
				}
				case "base64": {
					return {
						message: {
							[Locale.cs]: "Nejedná se o platné Base64",
							[Locale.en]: "Is not a valid Base64",
						},
					};
				}
				case "cidr": {
					return {
						message: {
							[Locale.cs]: "Nejedná se o platné CIDR",
							[Locale.en]: "Is not a valid CIDR",
						},
					};
				}
				case "jwt": {
					return {
						message: {
							[Locale.cs]: "Nejedná se o platné JWT",
							[Locale.en]: "Is not a valid JWT",
						},
					};
				}
				case "base64url": {
					return {
						message: {
							[Locale.cs]: "Nejedná se o platné Base64 URL",
							[Locale.en]: "Is not a valid Base64 URL",
						},
					};
				}
			}

			if ("startsWith" in issue.validation) {
				return {
					message: {
						[Locale.cs]: `Pole musí začínat na ${issue.validation.startsWith}`,
						[Locale.en]: `Field must begin with ${issue.validation.startsWith}`,
					},
				};
			} else if ("endsWith" in issue.validation) {
				return {
					message: {
						[Locale.cs]: `Pole musí končit na ${issue.validation.endsWith}`,
						[Locale.en]: `Field must end with ${issue.validation.endsWith}`,
					},
				};
			}

			return {
				message: {
					[Locale.cs]: `Pole musí obsahovat ${issue.validation.includes}${issue.validation.position !== undefined ? ` od pozice ${issue.validation.position}` : ""}`,
					[Locale.en]: `Field must contain ${issue.validation.includes}${issue.validation.position !== undefined ? ` from position ${issue.validation.position}` : ""}`,
				},
			};
		}
		case z.ZodIssueCode.invalid_date:
			return {
				message: {
					[Locale.cs]: "Pole musí být validní datum",
					[Locale.en]: "Is not a valid date",
				},
			};
		case z.ZodIssueCode.invalid_type:
			if (issue.expected !== "undefined" && issue.received === "undefined") {
				return {
					message: {
						[Locale.cs]: "Pole je povinné",
						[Locale.en]: "Field is required",
					},
				};
			}

			return {
				message: {
					[Locale.cs]: "Neplatná hodnota",
					[Locale.en]: "Invalid value",
				},
			};
		case z.ZodIssueCode.unrecognized_keys:
			return {
				message: {
					[Locale.cs]: "Pole nesmí obsahovat neznámé klíče",
					[Locale.en]: "Field must not include unrecognized keys",
				},
			};
		case z.ZodIssueCode.too_small: {
			const min = issue.inclusive ? issue.minimum : BigInt(issue.minimum) + BigInt(1);
			switch (issue.type) {
				case "set":
				case "array": {
					return {
						message: {
							[Locale.cs]: `Pole musí obsahovat alespoň ${min} ${inflectElement[Locale.cs](min)}`,
							[Locale.en]: `Field must contain atleast ${min} ${inflectElement[Locale.en](min)}`,
						},
					};
				}
				case "string":
					if (min === 1) {
						return {
							message: {
								[Locale.cs]: "Pole nesmí být prázdné",
								[Locale.en]: "Field must not be empty",
							},
						};
					}

					return {
						message: {
							[Locale.cs]: `Pole musí obsahovat alespoň ${min} ${inflectCharacter[Locale.cs](min)}`,
							[Locale.en]: `Pole musí obsahovat alespoň ${min} ${inflectCharacter[Locale.en](min)}`,
						},
					};
				case "bigint":
				case "number":
					return {
						message: {
							[Locale.cs]: `Pole musí být větší nebo rovno ${min}`,
							[Locale.en]: `Field must be equal or larger than ${min}`,
						},
					};
				case "date":
					return {
						message: {
							[Locale.cs]: `Datum musí být po ${toDateTimeMonthYearFormat(new Date(Number(min)), Locale.cs)}`,
							[Locale.en]: `Date must be after ${toDateTimeMonthYearFormat(new Date(Number(min)), Locale.en)}`,
						},
					};
			}
			break;
		}
		case z.ZodIssueCode.too_big: {
			const max = issue.inclusive ? issue.maximum : BigInt(issue.maximum) + BigInt(1);
			switch (issue.type) {
				case "set":
				case "array": {
					return {
						message: {
							[Locale.cs]: `Pole nesmí obsahovat více než ${max} ${inflectElement[Locale.cs](max)}`,
							[Locale.en]: `Field must not contain more than ${max} ${inflectElement[Locale.en](max)}`,
						},
					};
				}
				case "string":
					return {
						message: {
							[Locale.cs]: `Pole nesmí obsahovat více než ${max} ${inflectCharacter[Locale.cs](max)}`,
							[Locale.en]: `Field must not contain more than ${max} ${inflectCharacter[Locale.en](max)}`,
						},
					};
				case "bigint":
				case "number":
					return {
						message: {
							[Locale.cs]: `Pole musí být menší nebo rovno ${max}`,
							[Locale.en]: `Field must be equal or smaller than ${max}`,
						},
					};
				case "date":
					return {
						message: {
							[Locale.cs]: `Datum musí být před ${toDateTimeMonthYearFormat(new Date(Number(max)), Locale.cs)}`,
							[Locale.en]: `Date must be before ${toDateTimeMonthYearFormat(new Date(Number(max)), Locale.en)}`,
						},
					};
			}
			break;
		}
		case z.ZodIssueCode.invalid_literal:
		case z.ZodIssueCode.invalid_union:
		case z.ZodIssueCode.invalid_union_discriminator:
		case z.ZodIssueCode.invalid_enum_value:
		case z.ZodIssueCode.invalid_arguments:
		case z.ZodIssueCode.invalid_return_type:
		case z.ZodIssueCode.invalid_intersection_types:
		case z.ZodIssueCode.not_multiple_of:
		case z.ZodIssueCode.not_finite:
		case z.ZodIssueCode.custom:
			return { message: sameValueForEveryLocale(issue.message ?? "") };
	}
}

export function zodErrorMap(issue: ZodIssueOptionalMessage, ctx: ErrorMapCtx, locale: Locale): { message: string } {
	return {
		message: localizedErrorMap(issue, ctx).message[locale],
	};
}
